import {
    activityFeedLikeCardBody,
    activityFeedTwoLikesCardBody,
    activityFeedThreeOrMoreLikesCardBody,
    activityFeedMentionCardBody,
    activityFeedUserAtMentionedCardBody,
    activityFeedFlightReservationCardBody,
} from './createAdaptiveCard.locstring.json';
import loc, { format } from 'owa-localize';
import { ActivityType } from './ActivityFeedStorageItem';
import {
    ActivityFeedItem,
    LikeActivityFeedItem,
    MentionActivityFeedItem,
    UserAtMentionedActivityFeedItem,
    FlightReservationActivityFeedItem,
} from './ActivityFeedItem';
import { asOwaDateInUserTz } from 'owa-datetime';
import { getRelativeDateMessage } from 'owa-observable-datetime';

const assertUnreachable: (item: never) => never = () => {
    throw new Error();
};

export const createAdaptiveCard = (item: ActivityFeedItem) => {
    switch (item.type) {
        case ActivityType.Like:
            return createLikeAdaptiveCard(item);
        case ActivityType.AtMention:
            return createMentionAdaptiveCard(item);
        case ActivityType.TxpFlightReservation:
            return createFlightReservationAdaptiveCard(item);
        case ActivityType.UserAtMentioned:
            return createUserAtMentionedAdaptiveCard(item);
        case ActivityType.Unknown:
        case ActivityType.GroupMention:
        case ActivityType.MeMention:
        case ActivityType.FileMention:
        case ActivityType.GroupAtAllMention:
        case ActivityType.TxpRentalCarReservation:
        case ActivityType.TxpLodgingReservation:
        case ActivityType.TxpFoodEstablishmentReservation:
        case ActivityType.TxpEventReservation:
        case ActivityType.TxpParcelDelivery:
        case ActivityType.TxpInvoice:
        case ActivityType.TxpServiceReservation:
            return createUnknownAdaptiveCard();
    }
    // Switch exhaustiveness check
    // This never runs but is a hint to TypeScript that the switch must cover all cases
    return assertUnreachable(item);
};

export const createUnknownAdaptiveCard = () => null;

function formatBoldMarkdownString(text: string): any {
    var trimmedText = text.trim();
    if (trimmedText == '') {
        return trimmedText;
    } else {
        return `**${trimmedText}**`;
    }
}

function formatLikeString(item: LikeActivityFeedItem): string {
    if (item.payload.Actors.length == 1) {
        return format(
            loc(activityFeedLikeCardBody),
            formatBoldMarkdownString(item.payload.Actors[0].EmailAddress.Name),
            formatBoldMarkdownString(item.payload.TargetSubject)
        );
    } else if (item.payload.Actors.length == 2) {
        return format(
            loc(activityFeedTwoLikesCardBody),
            formatBoldMarkdownString(item.payload.Actors[0].EmailAddress.Name),
            formatBoldMarkdownString(item.payload.TargetSubject)
        );
    } else if (item.payload.Actors.length > 2) {
        return format(
            loc(activityFeedThreeOrMoreLikesCardBody),
            formatBoldMarkdownString(item.payload.Actors[0].EmailAddress.Name),
            formatBoldMarkdownString((item.payload.Actors.length - 1).toString()),
            formatBoldMarkdownString(item.payload.TargetSubject)
        );
    } else {
        // Return an empty string for now when the activity has 0 likers. Follow up workItem#49570
        return '';
    }
}

const fromFeedItemTemplate = (slots: {
    itemType: ActivityType;
    emailAddress: string;
    name: string;
    bodyText: string;
    timestamp: Date;
    selectAction: { type: string; [otherProps: string]: any };
}) => ({
    type: 'AdaptiveCard',
    body: [
        {
            type: 'Container',
            items: [
                {
                    type: 'ColumnSet',
                    columns: [
                        {
                            type: 'Column',
                            items: [
                                {
                                    type: 'ActivityPersona',
                                    emailAddress: slots.emailAddress,
                                    name: slots.name,
                                    itemType: slots.itemType,
                                },
                            ],
                            width: '40px',
                        },
                        {
                            type: 'Column',
                            items: [
                                {
                                    type: 'TextBlock',
                                    horizontalAlignment: 'Left',
                                    text: slots.bodyText,
                                    wrap: true,
                                    maxLines: 3,
                                },
                            ],
                            width: '175px',
                        },
                        {
                            type: 'Column',
                            items: [],
                            width: '16px',
                        },
                    ],
                },
                {
                    type: 'ColumnSet',
                    columns: [
                        {
                            type: 'Column',
                            items: [],
                            width: '40px',
                        },
                        {
                            type: 'Column',
                            items: [
                                {
                                    type: 'TextBlock',
                                    horizontalAlignment: 'Left',
                                    text: `${getRelativeDateMessage(
                                        asOwaDateInUserTz(slots.timestamp)
                                    )}`,
                                    wrap: true,
                                    maxLines: 2,
                                    isSubtle: true,
                                    weight: 'Bolder',
                                    size: 'Small',
                                },
                            ],
                            width: 'Auto',
                        },
                    ],
                },
            ],
        },
    ],
    selectAction: slots.selectAction,
    $schema: 'https://adaptivecards.io/schemas/adaptive-card.json',
    version: '1.0',
});

const defaultImage = (type: ActivityType) => ({
    type: 'Image',
    url: defaultImageLinksForTxp[type],
    size: 'Small',
});

const defaultImageLinksForTxp: { [key in ActivityType]?: string } = {
    TxpFlightReservation:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAZdEVYdFNvZnR3YXJlAHBhaW50Lm5ldCA0LjAuMjHxIGmVAAABEUlEQVRIS72VzQ2FMAyDOw0TsAALsAF3NmABBuDOAgzANCyTVxdFyovMIUCJ9ElgQlu7/CQRoXRdJ03T/LEsS77E+6+gIpjnuQw6TVMZeBgGads2X+L9V1BRwQTruuZDkeM4brmgooKY+r7Ph2fBTdQFFRWNCatH3XFBRYuNCRV1QUWLjynqIqHRYxt8TKiIizSOYxnAgkfSNkGzMUVcpG3bSvO+7/lcRM89NiYUXLA+AHdwnuvcA4i4QctHptiCC9YDdHJMUiZATNjMN0v36ZsJqkZUfZOrP6benr/p8YvGRMvjTwUTLT6eyOoBFRUfT3T1gIpK9R+OjefO6gEVgcaDVWPQ13/6iAcTWKKrF5H0A17faZ1ycpCnAAAAAElFTkSuQmCC',
};

export const fromHeroActionTemplate = (slots: {
    itemType: ActivityType;
    bodyText: string;
    timestamp: Date;
    heroActionText: string;
    heroAction: { type: string; [otherProps: string]: any };
}) => ({
    type: 'AdaptiveCard',
    body: [
        {
            type: 'ColumnSet',
            columns: [
                {
                    type: 'Column',
                    items: [defaultImage(slots.itemType)],
                    width: '28px',
                },
                {
                    type: 'Column',
                    items: [
                        {
                            type: 'TextBlock',
                            horizontalAlignment: 'Left',
                            text: slots.bodyText,
                            wrap: true,
                            maxLines: 2,
                        },
                        {
                            type: 'TextBlock',
                            horizontalAlignment: 'Left',
                            text: `${getRelativeDateMessage(asOwaDateInUserTz(slots.timestamp))}`,
                            wrap: true,
                            maxLines: 2,
                            isSubtle: true,
                            weight: 'Bolder',
                            size: 'Small',
                        },
                        {
                            type: 'ColumnSet',
                            columns: [
                                {
                                    type: 'Column',
                                    items: [
                                        {
                                            type: 'TextBlock',
                                            color: 'accent',
                                            size: 'Small',
                                            text: slots.heroActionText,
                                        },
                                    ],
                                    selectAction: slots.heroAction,
                                },
                            ],
                        },
                    ],
                    width: 'Auto',
                },
            ],
        },
    ],
    $schema: 'https://adaptivecards.io/schemas/adaptive-card.json',
    version: '1.0',
});

export const createLikeAdaptiveCard = (item: LikeActivityFeedItem) =>
    fromFeedItemTemplate({
        itemType: item.type,
        emailAddress: item.payload.Actors[0].EmailAddress.EmailAddress,
        name: item.payload.Actors[0].EmailAddress.Name,
        bodyText: formatLikeString(item),
        timestamp: new Date(item.payload.LatestTimestamp),
        selectAction: {
            type: 'Action.OpenMail',
            messageId: item.payload.TargetLogicalId,
        },
    });

export const createMentionAdaptiveCard = (item: MentionActivityFeedItem) =>
    fromFeedItemTemplate({
        itemType: item.type,
        emailAddress: item.payload.Actor.EmailAddress.EmailAddress,
        name: item.payload.Actor.EmailAddress.Name,
        bodyText: format(
            loc(activityFeedMentionCardBody),
            formatBoldMarkdownString(item.payload.Actor.EmailAddress.Name),
            formatBoldMarkdownString(item.payload.Subject)
        ),
        timestamp: new Date(item.timestamp),
        selectAction: {
            type: 'Action.OpenMail',
            messageId: item.payload.InternetMessageId,
        },
    });

export const createUserAtMentionedAdaptiveCard = (item: UserAtMentionedActivityFeedItem) =>
    fromFeedItemTemplate({
        itemType: item.type,
        emailAddress: item.payload.Mentioner.Email,
        name: item.payload.Mentioner.DisplayName,
        bodyText: format(
            loc(activityFeedUserAtMentionedCardBody),
            formatBoldMarkdownString(item.payload.Mentioner.DisplayName),
            formatBoldMarkdownString(item.payload.SourceFileName)
        ),
        timestamp: new Date(item.timestamp),
        selectAction: {
            type: 'Action.OpenUrl',
            url: item.payload.ObjectId + '?web=1',
        },
    });

export const createFlightReservationAdaptiveCard = (item: FlightReservationActivityFeedItem) =>
    fromHeroActionTemplate({
        itemType: item.type,
        bodyText: format(
            loc(activityFeedFlightReservationCardBody),
            formatBoldMarkdownString(item.payload.departureTerminal),
            formatBoldMarkdownString(
                getRelativeDateMessage(asOwaDateInUserTz(new Date(item.timestamp)))
            )
        ),
        timestamp: new Date(item.timestamp),
        heroActionText: 'Check-In',
        heroAction: {
            type: 'Action.OpenUrl',
            url: item.payload.checkinUrl,
        },
    });
